import React, { FC, useEffect, useState } from "react";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import CheckboxTree2, { Tree } from "../../../checkbox-tree/checkbox-tree2/checkbox-tree2";
import { VingCardDoor } from "../../../../models/VingCardDoor";
import ActivityFrame from "../../../activity-frame/activity-frame";
import { useFormikContext } from "formik";
import { getVingCardDoors } from "../../../../services/vingCardService";

interface DoorProps {
    onNext: (event: any) => void;
    onBack: (event: any) => void;
    setVingCardStepDescription: React.Dispatch<React.SetStateAction<{ cardType: string, door: string }>>;
}

const Door: FC<DoorProps> = ({ onNext, onBack, setVingCardStepDescription }) => {
    const { translate } = useLocale();
    const [tree, setTree] = useState<Tree[]>([]);
    const [busy, setBusy] = useState<boolean>(false);
    const { values, setFieldValue } = useFormikContext<any>();
    const doorSelected = (values.doorTree || []).some(group => group.children?.some(door => door.checked));

    useEffect(() => {
        buildDoorTree();
    }, [])

    const buildDoorTree = async () => {
        setBusy(true);
        let continueFetching = false;
        let nextPage = 1;
        const doors: VingCardDoor[] = [];

        do {
            const response = await getVingCardDoors({ pageSize: 100, goToPage: nextPage });
            doors.push(...response.rows);
            nextPage++;
            continueFetching = (response.pagination.pageNumber < response.pagination.totalPages);
        } while (continueFetching);

        // Find distinct door groups
        const distinctGroupIds = Array.from(new Set(doors.map(door => door.groupId)));
        const doorGroups = distinctGroupIds.map(groupId => {
            return {
                id: groupId,
                name: doors.find(door => door.groupId === groupId)?.group || translate.byKey(TranslationKeys.unknown),
            }
        });

        // Build tree structure
        const doorTree: Tree[] = doorGroups.map(group => {
            return {
                id: group.id,
                label: group.name,
                lookupname: `doorGroup${group.id}`,
                clickable: true,
                canExpand: true,
                children: doors.filter(door => door.groupId === group.id).map((door): Tree => {
                    return {
                        id: door.id.toString(),
                        label: door.name,
                        lookupname: `doorGroup${group.id}/door${door.id}`,
                        clickable: true,
                    }
                })
            }
        });

        setTree(doorTree);
        setBusy(false);
    }

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        const newTree: Tree[] = values.doorTree || [];
        const selectedDoors = newTree.flatMap(group =>
            group.children ? group.children.filter(door => door.checked).map(door => door.label) : []
        );
        setFieldValue('selectedDoors', selectedDoors);
        setVingCardStepDescription(prevState => ({ ...prevState, door: translate.byKeyFormatted(TranslationKeys.selectedWithCount, { count: selectedDoors.length }) }));
        onNext(e);
    }

    const onExapand = (_: Tree) => {
        // All data is already loaded, so no need to fetch more data on expand
    }

    return <div className="pr-3 py-3 h-full flex flex-col relative">
        <div className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`} >
            {translate.byKey(TranslationKeys.selectDoors)}
        </div>
        <ActivityFrame busy={busy} busyMessage={translate.byKey(TranslationKeys.loadingDoorsPleaseWait)} className={`flex  ${busy ? 'h-full w-full justify-center' : ''} flex-wrap content-center`}>
            <CheckboxTree2 tree={tree} name={"doorTree"} onExpand={onExapand} />
            <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
                <div className="flex flex-row mt-4">
                    <button
                        className={`secondary secondary-inverse py-1 px-5`}
                        type="button"
                        onClick={onBack}>{translate.byKey(TranslationKeys.back)}</button>
                </div>
                <div className="flex flex-row justify-end mt-4 pl-3">
                    <button
                        id="nextButton"
                        data-cy="nextButton"
                        className={`py-1 px-5 secondary ${doorSelected ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                        type="button"
                        disabled={!doorSelected}
                        onClick={onLocalNext}>{translate.byKey(TranslationKeys.next)}</button>
                </div>
            </div>
        </ActivityFrame>
    </div>
}

export default Door;